import React, { useEffect, useState } from "react";
import axios from "axios";
import { config } from "../../../../config";
import { Link } from "react-router-dom";
import UpdateLead from "./UpdateLead";

const apiUrl = config.apiUrl;

const ViewLead = () => {
  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingApplication, setEditingApplication] = useState(null);
  const [formData, setFormData] = useState({
    slug: "",
    name: "",
    email: "",
    whatsapp: "",
  });

  // Filter states
  const [dateFilter, setDateFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Date format function
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Fetch applications from API
  useEffect(() => {
    const fetchApplications = async () => {
      try {
        const response = await axios.get(`${apiUrl}/applications`);
        const applicationsWithDate = response.data.applications.map(app => ({
          ...app,
          createdDate: app.created_at ? formatDate(app.created_at) : formatDate(new Date()),
          createdDateTime: app.created_at ? new Date(app.created_at) : new Date()
        }));
        setApplications(applicationsWithDate);
        setFilteredApplications(applicationsWithDate);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching applications:", err);
        setError("Failed to load applications.");
        setLoading(false);
      }
    };

    fetchApplications();
  }, []);

  // Apply date filter
  useEffect(() => {
    let filtered = applications;

    if (dateFilter) {
      const filterDate = formatDate(dateFilter);
      filtered = filtered.filter(app => app.createdDate === filterDate);
    }

    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      filtered = filtered.filter(app => {
        const appDate = app.createdDateTime;
        return appDate >= start && appDate <= end;
      });
    }

    setFilteredApplications(filtered);
    setCurrentPage(1); // Reset to first page when filter changes
  }, [dateFilter, startDate, endDate, applications]);

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredApplications.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredApplications.length / itemsPerPage);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Next page
  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Previous page
  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Handle Delete
  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this application?")) {
      try {
        await axios.delete(`${apiUrl}/applicationdelete/${id}`);
        setApplications((prev) => prev.filter((app) => app.id !== id));
      } catch (err) {
        console.error("Error deleting application:", err);
        setError("Failed to delete application.");
      }
    }
  };

  // Handle Edit Button Click
  const handleEditClick = (app) => {
    setEditingApplication(app.id);
    setFormData({
      slug: app.slug,
      name: app.name,
      email: app.email,
      whatsapp: app.phone,
    });
  };

  // Handle Input Changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle Form Submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!editingApplication) {
      setError("No application selected for editing.");
      return;
    }

    try {
      await axios.put(`${apiUrl}/applicationsupdate/${editingApplication}`, formData);
      alert("Application updated successfully");
      setApplications((prev) =>
        prev.map((app) =>
          app.id === editingApplication ? { ...app, ...formData } : app
        )
      );
      setEditingApplication(null);
    } catch (err) {
      console.error("Error updating application:", err);
      setError("Failed to update application.");
    }
  };

  // Get last 3 days lead counts
  const getLast3DaysLeadCounts = () => {
    const counts = {};
    const today = new Date();
    
    // Create an array of last 3 dates including today
    const last3Days = [];
    for (let i = 0; i < 3; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      last3Days.push(formatDate(date));
    }

    // Count leads for each of the last 3 days
    last3Days.forEach(date => {
      counts[date] = applications.filter(app => app.createdDate === date).length;
    });

    return counts;
  };

  const last3DaysCounts = getLast3DaysLeadCounts();

  // Reset filters
  const resetFilters = () => {
    setDateFilter("");
    setStartDate("");
    setEndDate("");
    setCurrentPage(1);
  };

  // Generate page numbers for pagination
  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  // Show limited page numbers (max 5 at a time)
  const getDisplayedPages = () => {
    const maxPages = 5;
    if (totalPages <= maxPages) return pageNumbers;
    
    const half = Math.floor(maxPages / 2);
    let start = currentPage - half;
    let end = currentPage + half;
    
    if (start < 1) {
      start = 1;
      end = maxPages;
    }
    
    if (end > totalPages) {
      end = totalPages;
      start = Math.max(1, end - maxPages + 1);
    }
    
    return pageNumbers.slice(start - 1, end);
  };

  // Function to get today, yesterday, day before in formatted date
  const getDateLabels = () => {
    const today = formatDate(new Date());
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const dayBefore = new Date();
    dayBefore.setDate(dayBefore.getDate() - 2);
    
    return {
      today: today,
      yesterday: formatDate(yesterday),
      dayBefore: formatDate(dayBefore)
    };
  };

  const dateLabels = getDateLabels();

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="p-6 max-w-6xl mx-auto bg-white shadow rounded">
      <h2 className="text-2xl font-bold mb-4 text-center">Application List</h2>
      
      {/* Last 3 Days Lead Counts */}
      <div className="mb-6 p-4 bg-gray-50 rounded">
        <h3 className="text-lg font-bold mb-3">Last 3 Days Lead Counts</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Object.entries(last3DaysCounts).map(([date, count]) => {
            let dayLabel = "";
            if (date === dateLabels.today) dayLabel = " (Today)";
            else if (date === dateLabels.yesterday) dayLabel = " (Yesterday)";
            else if (date === dateLabels.dayBefore) dayLabel = " (Day Before)";
            
            return (
              <div key={date} className="bg-white p-4 rounded shadow border">
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-gray-700">
                    {date}
                    <span className="text-blue-600 text-sm ml-1">{dayLabel}</span>
                  </span>
                  <span className={`text-lg font-bold ${
                    count > 0 ? 'text-green-600' : 'text-gray-500'
                  }`}>
                    {count}
                  </span>
                </div>
                <div className="mt-2 text-sm text-gray-500">
                  {count === 0 ? 'No leads' : `${count} lead${count > 1 ? 's' : ''}`}
                </div>
                {count > 0 && (
                  <div className="mt-2">
                    <button
                      onClick={() => {
                        // Convert back to input date format for filtering
                        const [day, month, year] = date.split('/');
                        const filterDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
                        setDateFilter(filterDate);
                        setStartDate("");
                        setEndDate("");
                      }}
                      className="text-blue-600 hover:text-blue-800 text-sm underline"
                    >
                      View these leads
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        
        {/* Total leads summary */}
        <div className="mt-4 p-3 bg-blue-50 rounded">
          <div className="flex justify-between items-center">
            <span className="font-semibold text-blue-800">Total (Last 3 days):</span>
            <span className="font-bold text-blue-800">
              {Object.values(last3DaysCounts).reduce((sum, count) => sum + count, 0)} leads
            </span>
          </div>
        </div>
      </div>

      {/* Filter Section */}
      <div className="mb-6 p-4 bg-gray-50 rounded">
        <h3 className="text-lg font-bold mb-3">Filter Leads</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Single Date Filter */}
          <div>
            <label className="block text-sm font-medium mb-1">Filter by Date:</label>
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="w-full p-2 border rounded"
            />
          </div>

          {/* Date Range Filter */}
          <div>
            <label className="block text-sm font-medium mb-1">Start Date:</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full p-2 border rounded"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">End Date:</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full p-2 border rounded"
            />
          </div>

          <div className="flex items-end">
            <button
              onClick={resetFilters}
              className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
            >
              Reset Filters
            </button>
          </div>
        </div>

        {/* Results Count */}
        <div className="mt-3">
          <p className="text-sm text-gray-600">
            Showing {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredApplications.length)} of {filteredApplications.length} leads
            {dateFilter && ` for ${formatDate(dateFilter)}`}
            {startDate && endDate && ` from ${formatDate(startDate)} to ${formatDate(endDate)}`}
          </p>
        </div>
      </div>

      {error && <p className="text-red-500">{error}</p>}
      
      <table className="w-full border-collapse border border-gray-300">
        <thead>
          <tr>
            <th className="border border-gray-300 px-4 py-2">Slug</th>
            <th className="border border-gray-300 px-4 py-2">Name</th>
            <th className="border border-gray-300 px-4 py-2">Email</th>
            <th className="border border-gray-300 px-4 py-2">WhatsApp</th>
            <th className="border border-gray-300 px-4 py-2">Created Date</th>
            <th className="border border-gray-300 px-4 py-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {currentItems.map((app) => (
            <tr key={app.id}>
              <td className="border border-gray-300 px-4 py-2">{app.slug}</td>
              <td className="border border-gray-300 px-4 py-2">{app.name}</td>
              <td className="border border-gray-300 px-4 py-2">{app.email}</td>
              <td className="border border-gray-300 px-4 py-2">{app.phone}</td>
              <td className="border border-gray-300 px-4 py-2">{app.createdDate}</td>
              <td className="flex flex-col gap-2 border border-gray-300 px-4 py-2">
                <button
                  onClick={() => handleEditClick(app)}
                  className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(app.id)}
                  className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700"
                >
                  Delete
                </button>
                <Link
                  to={`/application/${app.slug}`}
                  className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 text-center"
                >
                  View
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {currentItems.length === 0 && !loading && (
        <div className="text-center py-4 text-gray-500">
          No leads found for the selected filter criteria.
        </div>
      )}

      {/* Pagination */}
      {filteredApplications.length > itemsPerPage && (
        <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-sm text-gray-600">
            Page {currentPage} of {totalPages} • {filteredApplications.length} total leads
          </div>
          
          <div className="flex items-center gap-2">
            {/* Previous Button */}
            <button
              onClick={prevPage}
              disabled={currentPage === 1}
              className={`px-3 py-1 rounded ${
                currentPage === 1 
                  ? 'bg-gray-300 cursor-not-allowed' 
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              Previous
            </button>

            {/* Page Numbers */}
            <div className="flex gap-1">
              {getDisplayedPages().map(number => (
                <button
                  key={number}
                  onClick={() => paginate(number)}
                  className={`px-3 py-1 rounded ${
                    currentPage === number
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 hover:bg-gray-300'
                  }`}
                >
                  {number}
                </button>
              ))}
              
              {/* Ellipsis for more pages */}
              {totalPages > 5 && currentPage < totalPages - 2 && (
                <span className="px-2 py-1">...</span>
              )}
            </div>

            {/* Next Button */}
            <button
              onClick={nextPage}
              disabled={currentPage === totalPages}
              className={`px-3 py-1 rounded ${
                currentPage === totalPages
                  ? 'bg-gray-300 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              Next
            </button>
          </div>

          <div className="text-sm text-gray-600">
            {itemsPerPage} items per page
          </div>
        </div>
      )}

      {editingApplication && (
        <UpdateLead
          formData={formData}
          handleChange={handleChange}
          handleSubmit={handleSubmit}
          loading={loading}
          error={error}
        />
      )}
    </div>
  );
};

export default ViewLead;